import { ChangeEvent, FC, ReactNode, useState } from 'react';

import { Input, Spinner, theme, Typography } from '@/shared/design-system';

import { SuggestionItem, SuggestionList, SuggestionLogo, SuggestionTextColumn } from './styled';

export interface ContactSuggestion {
  id: string;
  label: string;
  supportText?: string | null;
  iconUrl?: string | null;
}

interface ContactAutocompleteProps {
  label: string;
  createNewLabel?: string;
  required?: boolean;
  placeholder?: string;
  value: string;
  suggestions: ContactSuggestion[];
  isLoading?: boolean;
  endIcon?: ReactNode;
  onChange: (_value: string) => void;
  onSelect: (_suggestion: ContactSuggestion) => void;
  onCreateNew?: (_value: string) => void;
}

export const ContactAutocomplete: FC<ContactAutocompleteProps> = ({
  label,
  required,
  placeholder,
  value,
  suggestions,
  isLoading,
  endIcon,
  createNewLabel,
  onChange,
  onSelect,
  onCreateNew,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const trimmed = value.trim();
  const showCreateNew =
    Boolean(createNewLabel && trimmed) &&
    !suggestions.some((s) => s.label.trim().toLowerCase() === trimmed.toLowerCase());
  const showList =
    isOpen && Boolean(trimmed) && (isLoading || suggestions.length > 0 || showCreateNew);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <Input
        size="lg"
        label={label}
        required={required}
        placeholder={placeholder}
        value={value}
        endIcon={endIcon}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
      />

      {showList && (
        <SuggestionList onMouseDown={(event) => event.preventDefault()}>
          {isLoading ? (
            <SuggestionItem as="div">
              <Spinner />
              <Typography variant="body3" color={theme.colors.text.secondary}>
                Searching…
              </Typography>
            </SuggestionItem>
          ) : (
            suggestions.map((suggestion) => (
              <SuggestionItem
                key={suggestion.id}
                type="button"
                onClick={() => {
                  onSelect(suggestion);
                  setIsOpen(false);
                }}
              >
                {suggestion.iconUrl && (
                  <SuggestionLogo src={suggestion.iconUrl} alt="" aria-hidden />
                )}
                <SuggestionTextColumn>
                  <Typography variant="body3">{suggestion.label}</Typography>
                  {suggestion.supportText && (
                    <Typography variant="body4" color={theme.colors.text.secondary}>
                      {suggestion.supportText}
                    </Typography>
                  )}
                </SuggestionTextColumn>
              </SuggestionItem>
            ))
          )}

          {!isLoading && showCreateNew && (
            <SuggestionItem
              type="button"
              onClick={() => {
                if (onCreateNew) onCreateNew(trimmed);
                else onChange(trimmed);
                setIsOpen(false);
              }}
            >
              <SuggestionTextColumn>
                <Typography variant="body3" color={theme.colors.text.brand}>
                  {createNewLabel} “{trimmed}”
                </Typography>
              </SuggestionTextColumn>
            </SuggestionItem>
          )}
        </SuggestionList>
      )}
    </div>
  );
};
